<template>
	<div data-component="deal-pack-edit">
		<validation-observer
			data-element="form"
			tag="form"
			ref="form"
			@submit.prevent="submit"
			v-if="getFormIsVisible"
		>
			<input-field
				name="Title"
				type="text"
				rules="required"
				placeholder="Deal pack title"
				v-model="dealPack.title"
			/>
			<input-field
				name="Description"
				type="text"
				rules="required"
				placeholder="Deal pack description"
				v-model="dealPack.description"
			/>
			<wysiwyg-field
				name="About"
				type="text"
				rules="required"
				v-model="dealPack.introduction"
			/>
			<range-field
				name="Experience level"
				rules="required"
				v-if="loadedForm"
				:optionLabel="['value', 'text']"
				:options="experienceLevelOptions.slice(0,8)"
				:labelMarks="experienceLevelMarks.slice(0,8)"
				v-model="experienceLevelValues"
			/>
			<select-field
				name="Bidding systems"
				:options="biddingSystemTypeOptions"
				:emptyAllowed="false"
				v-model="biddingSystemType"
			/>
			<select-field
				v-if="getShowSpecificBiddingSystems"
				name="Specific bidding systems (Blank for any)"
				:options="biddingSystemOptions"
				v-model="dealPack.biddingSystems"
				multiple
			/>
			<focus-keywords
				:focusVal="dealPack.focus"
				:keywordsVal="dealPack.keywords"
				:formLoaded="loadedForm"
				:entryRoute="entryRoute"
				@onKeyword="updateKeywords"
				@onFocus="updateFocus"
			/>

			<v2-checkbox-item :value="dealPack.isFree" :selected="!!dealPack.isFree" @change="dealPack.isFree = !dealPack.isFree">
				Is this a free deal pack?
			</v2-checkbox-item>

			<input-field
				v-if="!dealPack.isFree"
				name="Cost"
				type="number"
				:step="0.01"
				rules="required|minValue:0.30|maxValue:99999.99"
				v-model="dealPack.cost"
				placeholder="Deal pack cost"
			/>

			<v2-checkbox-item
				v-if="!dealPack.isFree"
				:value="dealPack.enableDiscountForSubs"
				:selected="!!dealPack.enableDiscountForSubs"
				@change="dealPack.enableDiscountForSubs = !dealPack.enableDiscountForSubs">
				Enable discount for Handle subscribers?
			</v2-checkbox-item>

			<div v-if="dealPack.enableDiscountForSubs && !dealPack.isFree" class="discount-options">
				<v2-checkbox-item
					:value="dealPack.freeForHandleSubs"
					:selected="!!dealPack.freeForHandleSubs"
					@change="dealPack.freeForHandleSubs = !dealPack.freeForHandleSubs">
					Free for Handle subscribers
				</v2-checkbox-item>
				<input-field
					name="Discount percentage, %"
					type="number"
					:step="1"
					:readonly="dealPack.freeForHandleSubs"
					:rules="getDiscountPercentageRules"
					v-model="dealPack.discountPercentage"
					placeholder="Enter percentage"
				/>
			</div>

			<upload
				fileType="image"
				:helpText="uploadHelpText"
				:form.sync="form"
				:media="dealPack.media"
				@deleteMedia="deleteMedia"
			/>
			<text-note
				v-if="dealPack.approvalNote"
				name="Approval note"
				type="text"
				:value="dealPack.approvalNote"
			/>

		</validation-observer>
	</div>
</template>

<script>
	import V2CheckboxItem from '@/components/v2/ui/checkbox/V2CheckboxItem.vue';

	import InputField            from '@/components/forms/InputField';
	import SelectField           from '@/components/forms/SelectField';
	import RangeField            from '@/components/forms/RangeField';
	import Upload              from '@/components/uploads/Upload';
	import TextNote              from '@/components/forms/TextNote';
	import FocusKeywords         from '@/components/forms/FocusKeywords';
	import actionClick           from '@/mixins/actionClick';
	import invalidFormMessage    from '@/mixins/invalidFormMessage';
	import routeParams           from '@/mixins/routeParams';
	import api                   from '@/services/api';
	import { experienceLevels }  from '@/consts';
	import { biddingSystems }    from '@/consts';

	export default {
		components: {
			V2CheckboxItem,
			InputField,
			WysiwygField: () => import('@/components/forms/WysiwygField'),
			SelectField,
			FocusKeywords,
			TextNote,
			RangeField,
			Upload
		},
		mixins: [actionClick, invalidFormMessage, routeParams],
		props: {
			entryRoute: {
				type: String,
				default: 'teaching'
			},
			isCreate: {
				type: Boolean,
				default: false
			},
			cancelRoute: {
				type: String,
				default: ''
			},
			dealPackId: {
				type: String,
				default: ''
			}
		},
		data: () => ({
			form: undefined,
			uploadHelpText: [
				'Images should be in jpg, jpeg or png file formats',
				'Images should be less than 2MB'
			],
			experienceLevelOptions: experienceLevels,
			experienceLevelValues: [1, 8],
			experienceLevelMarks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
			keywords: [],
			loadedKeywords: false,
			loadedForm: false,
			biddingSystemType: 'specific',
			biddingSystemTypeOptions: [
				{
					text: 'Specific bidding systems',
					value: 'specific'
				},
				{
					text: 'None',
					value: 'none'
				}
			],
			biddingSystemOptions: biddingSystems.map((biddingSystem, index) => {
				return {
					text: biddingSystem.value,
					value: biddingSystem.value
				};
			}),
			dealPack: {
				title:                 '',
				description:           '',
				introduction:          '',
				minExperienceLevel:    '',
				maxExperienceLevel:    '',
				biddingSystems:        [],
				keywords:              [],
				focus:	               [],
				status:                '',
				approval:              '',
				cost:                  null,
				isFree:                false,
				enableDiscountForSubs: false,
				freeForHandleSubs:     false,
				discountPercentage:    null
			}
		}),
		computed: {
			getActions () {
				return {
					primary: [
						{
							text: this.isCreate ? 'Create' : 'Save',
							type: 'submit'
						}
					],
					secondary: [
						{
							text: 'Cancel',
							route: this.cancelRoute
						}
					]
				};
			},
			getFormIsVisible () {
				if (this.isCreate) {
					return true;
				}
				return this.dealPack.title !== '';
			},
			getShowSpecificBiddingSystems () {
				return Array.isArray(this.dealPack.biddingSystems);
			},
			getDiscountPercentageRules () {
				if (this.dealPack.freeForHandleSubs) {
					return {};
				}
				return {
					required: true,
					between: {
						min: 0,
						max: 100
					}
				};
			},
			getBreadcrumbs () {
				return [
					(this.getContext === 'admin' ?
						{
							path: '/admin',
							text: 'Admin'
						} : false
					),
					(this.getContext === 'teaching' ?
						{
							path: '/teaching',
							text: 'Teaching'
						} : false
					),
					{
						path: this.getDealPacksRoute,
						text: 'Deal Packs'
					},
					(!this.isCreate ?
						{
							path: this.getDealPackRoute,
							text: this.dealPack?.title?.length ? this.dealPack.title : 'Deal Pack'
						} : false
					),
					(!this.isCreate ?
						{
							path: `${this.getDealPackRoute}/edit`,
							text: 'Edit'
						} : false
					),
					(this.isCreate ?
						{
							path: `${this.getDealPacksRoute}/create`,
							text: 'New Deal Pack'
						} : false
					)
				].filter(Boolean);
			}
		},
		watch: {
			experienceLevelValues (newVal, oldVal) {
				if (newVal !== oldVal) {
					this.dealPack.minExperienceLevel = newVal[0];
					this.dealPack.maxExperienceLevel = newVal[1];
				}
			},
			'dealPack.biddingSystems' (newVal) {
				if (Array.isArray(newVal)) {
					this.biddingSystemType = 'specific';
					return;
				}
				this.biddingSystemType = 'none';
			},
			biddingSystemType (newVal) {
				if (newVal === 'specific') {
					if (Array.isArray(this.dealPack.biddingSystems)) {
						return false;
					}
					this.dealPack.biddingSystems = [];
					return;
				}
				if (this.dealPack.biddingSystems === null) {
					return false;
				}
				this.dealPack.biddingSystems = null;
			},
			'dealPack.isFree' (newVal) {
				if (newVal) {
					this.dealPack.cost = null;
					this.dealPack.discountPercentage = null;
					this.dealPack.enableDiscountForSubs = false;
					this.dealPack.freeForHandleSubs = false;
				}
			},
			'dealPack.freeForHandleSubs' (newVal) {
				if (newVal) {
					this.dealPack.discountPercentage = null;
				}
			}
		},
		mounted () {
			if (this.isCreate) {
				this.dealPack.status = 'draft';
				this.experienceLevelValues = [1, 1];
				this.loadedKeywords = true;
				this.loadedForm = true;
				this.$store.commit('ui/setLoadingIsHidden');
				this.$store.commit('ui/setBreadcrumbs', {
					breadcrumbs: this.getBreadcrumbs
				});
				return;
			}
			this.setDealPack();
		},
		methods: {

			async createMedia (dealPackId) {
				const media = await api[this.entryRoute].createMediaByDealPackById({
					dealPackId: dealPackId,
					media: this.form
				});
				if (!media) {
					return false;
				}
				return media;
			},

			async deleteMedia () {
				if (!this?.dealPack?.media?.[0]?.id) {
					return false;
				}

				this.dealPack.media = [];
			},

			updateFocus (val) {
				this.dealPack.focus = val;
			},
			updateKeywords (val) {
				this.dealPack.keywords = val;
			},
			async setDealPack () {
				const dealPack = await api[this.entryRoute].getDealPackById({
					dealPackId: this.dealPackId
				});
				if (!dealPack) {
					this.$store.commit('ui/showError');
					return false;
				}
				if (Number(dealPack.cost) === 0) {
					dealPack.isFree = true;
				}
				this.dealPack = {
					...this.dealPack,
					...dealPack
				};
				this.experienceLevelValues = [this.dealPack.minExperienceLevel, this.dealPack.maxExperienceLevel];
				this.loadedForm = true;
				this.$store.commit('ui/setBreadcrumbs', {
					breadcrumbs: this.getBreadcrumbs
				});
				this.$store.commit('ui/setLoadingIsHidden');
			},
			async submit () {
				let dealPack;
				if (this.dealPack.isFree) {
					this.dealPack.cost = 0;
				}
				const valid = await this.$refs.form.validate();
				if (!valid) {
					this.showInvalidFormMessage();
					return;
				}
				if (this.isCreate) {
					dealPack = await api.teaching.createDealPack({
						dealPack: this.dealPack
					});

					if (!dealPack.id) {
						return;
					}
				} else {
					dealPack = await api[this.entryRoute].updateDealPackById({
						dealPack: this.dealPack,
						dealPackId: this.dealPackId
					});
					if (!dealPack) {
						return;
					}
				}

        if (this.form) {
					const media = await this.createMedia(dealPack.id);
					if (!media) {
						this.$router.push(this.getUserRoute);
						this.$store.commit('ui/showNotification', {
							notification: 'The file could not be uploaded. Please edit the deal pack and try again.'
						});
						return;
					}
				}

				await this.$router.push(`/${this.entryRoute}/deal-packs/${dealPack.id}`);
				this.$store.commit('ui/showNotification', {
					notification: 'The deal pack has been ' + (this.isCreate ? 'created' : 'updated')
				});
			}
		}
	};

</script>

<style lang="scss" scoped>
::v-deep [data-component=input-field] > div {

  label {
    @include font(18px, $c-darkest-navy, 400, 23px);
    margin-bottom: 6px !important;
  }

  input {
    padding: 10px 12px !important;
    border-radius: 4px !important;
    border: 1px solid $c-gray-l;
    @include font(16px, $c-darkest-navy, 400, 20px);

  }
}

::v-deep [data-component=wysiwyg-field] > div {

  label {
    @include font(18px, $c-darkest-navy, 400, 23px);
    margin-bottom: 6px !important;
  }

  .quill-editor {
    background-color: $c-white-l;
    font-family: "DM Sans", sans-serif !important;

    p {
      font-family: "DM Sans", sans-serif !important;
    }
  }

  .ql-container.ql-snow {
    border: 1px solid $c-gray-l;
  }

  .ql-toolbar.ql-snow {
    border: 1px solid $c-gray-l;
  }
}

::v-deep [data-component=range-field] > span {

  label {
    @include font(18px, $c-darkest-navy, 400, 23px);
    margin-bottom: 6px !important;
  }

  input {
    padding: 10px 12px !important;
    border-radius: 4px !important;
    border: 1px solid $c-gray-l;
    @include font(16px, $c-darkest-navy, 400, 20px);
  }

  .vue-slider-process {
    background-color: $c-medium-navy !important;
  }

  .vue-slider:hover .vue-slider-process {
    background-color: $c-darkest-navy !important;
  }

  .vue-slider-dot-handle {
    border: 2px solid $c-medium-navy !important;
  }

  .vue-slider-mark-step-active {
    box-shadow: 0 0 0 2px $c-medium-navy !important;
  }

  .vue-slider-dot-handle-focus {
    box-shadow: 0 0 0 5px rgb(186, 193, 202, 0.2);
  }
}

::v-deep [data-component=select-field] > div {

  label {
    @include font(18px, $c-darkest-navy, 400, 23px);
    margin-bottom: 6px !important;
  }

  .multiselect__tags {
    border-radius: 4px !important;
    border: 1px solid $c-gray-l;
    @include font(16px, $c-darkest-navy, 400, 20px);
  }
}

.discount-options {
	margin-top: 16px;

	::v-deep [data-component=input-field] {
		width: 100%;
		max-width: 250px;

		@media (max-width: 767px) {
			max-width: 100%;
		}
	}
}
</style>
